import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, InputNumber, Modal, Spin, Table, Tag } from 'antd';
import { ExperimentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getEtaBacktest } from '../modules/routes/api';

/**
 * OVO-266 — ETA backtest: replays a completed route three ways (old plan /
 * new service-time formula / actual) so the operator can SEE that the new
 * parameters produce tighter ETAs, and tune them live before saving anything.
 * Route 2155 reference: old plan MAE 102 min → defaults 52 → tuned 25.
 */
export default function EtaBacktestModal({ routeId, open, onClose }) {
  const { t } = useTranslation(['routes', 'common']);
  const [params, setParams] = useState({
    taskPickupMins: null,
    stopPickupMins: null,
    taskDeliveryMins: null,
    stopDeliveryMins: null,
    speedFactor: null,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    if (!routeId) return;
    setLoading(true);
    try {
      const query = Object.fromEntries(Object.entries(params).filter(([, v]) => v != null));
      const { response } = await getEtaBacktest(routeId, query);
      if (response?.summary) setData(response);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, JSON.stringify(params)]);

  useEffect(() => {
    if (open) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const s = data?.summary;
  const improved = s && s.maeNewMin != null && s.maeOldMin != null && s.maeNewMin < s.maeOldMin;

  const paramInput = (key, label, step = 0.5) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {label}
      <InputNumber
        size="small"
        min={0}
        step={step}
        style={{ width: 80 }}
        value={params[key]}
        placeholder="auto"
        onChange={(v) => setParams((p) => ({ ...p, [key]: v }))}
      />
    </span>
  );

  const columns = [
    { title: '#', dataIndex: 'seq', width: 50 },
    { title: t('etaBacktest.colType'), dataIndex: 'type', width: 80, render: (v) => <Tag>{v}</Tag> },
    { title: t('etaBacktest.colOrder'), dataIndex: 'orderCode', width: 120 },
    { title: t('etaBacktest.colOld'), dataIndex: 'oldFinish', width: 80 },
    { title: t('etaBacktest.colNew'), dataIndex: 'newFinish', width: 80 },
    { title: t('etaBacktest.colActual'), dataIndex: 'actual', width: 80 },
    {
      title: 'Δ old',
      dataIndex: 'driftOldMin',
      width: 80,
      render: (v) =>
        v == null ? '—' : <span style={{ color: Math.abs(v) > 30 ? '#cf1322' : '#888' }}>{v > 0 ? `+${v}` : v}′</span>,
    },
    {
      title: 'Δ new',
      dataIndex: 'driftNewMin',
      width: 80,
      render: (v) =>
        v == null ? (
          '—'
        ) : (
          <span style={{ color: Math.abs(v) > 30 ? '#cf1322' : '#389e0d' }}>{v > 0 ? `+${v}` : v}′</span>
        ),
    },
  ];

  return (
    <Modal
      title={
        <span>
          <ExperimentOutlined style={{ marginRight: 8 }} />
          {t('etaBacktest.title', { id: routeId })}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
    >
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {paramInput('stopPickupMins', t('etaBacktest.stopPickup'))}
        {paramInput('taskPickupMins', t('etaBacktest.taskPickup'))}
        {paramInput('stopDeliveryMins', t('etaBacktest.stopDelivery'))}
        {paramInput('taskDeliveryMins', t('etaBacktest.taskDelivery'))}
        {paramInput('speedFactor', t('etaBacktest.speed'), 0.05)}
        <Button type="primary" size="small" loading={loading} onClick={run}>
          {t('etaBacktest.recalculate')}
        </Button>
      </div>
      {data && (
        <Alert
          style={{ marginBottom: 12 }}
          type={improved ? 'success' : 'info'}
          showIcon
          message={
            <span>
              {t('etaBacktest.mae')}:{' '}
              <b>
                {s.maeOldMin}′ → {s.maeNewMin}′
              </b>{' '}
              · {t('etaBacktest.lastStopDrift')}:{' '}
              <b>
                {s.finalDriftOldMin > 0 ? `+${s.finalDriftOldMin}` : s.finalDriftOldMin}′ →{' '}
                {s.finalDriftNewMin > 0 ? `+${s.finalDriftNewMin}` : s.finalDriftNewMin}′
              </b>{' '}
              · {data.fleetTag} × {data.speedFactor}
            </span>
          }
        />
      )}
      <Spin spinning={loading}>
        <Table
          rowKey="seq"
          size="small"
          columns={columns}
          dataSource={data?.rows || []}
          pagination={false}
          scroll={{ y: 380 }}
        />
      </Spin>
      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>{t('etaBacktest.footnote')}</div>
    </Modal>
  );
}
